import { useState, useEffect } from 'react';
import { Joyride, Step, EVENTS, ACTIONS, STATUS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { useUserRoles } from '@/hooks/useUserRoles';
import { CustomTooltip } from './CustomTooltip';
import { ROUTES } from '@/config/routes.config';

// Extend Step to include custom route info for navigation
export type AppTourStep = Step & {
  route?: string;
  delayBefore?: number;
};

export function OnboardingTutorial() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authenticatedEmployee, authority } = useAuthStore();
  const { isAdmin, isGlobalHR, isCountryHR } = useUserRoles();
  
  const [runTutorial, setRunTutorial] = useState(false);
  const [steps, setSteps] = useState<AppTourStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [hasDismissedSession, setHasDismissedSession] = useState(false);

  useEffect(() => {
    const roleKey = user?.employeeRole || 'employee';
    const storageKey = `tour_seen_${user?.id}_${roleKey}`;
    let hasSeenTour = false;
    try {
      hasSeenTour = localStorage.getItem(storageKey) === 'true';
    } catch {
      // Ignore if localStorage is unavailable
    }

    if (!hasSeenTour && !hasDismissedSession && user?.id && authenticatedEmployee && authority && !isAdmin) {
      // Build the massive step array based on roles
      const builtSteps: AppTourStep[] = [];

      // --- Dashboard ---
      builtSteps.push({
        target: '[data-tour="tour-dashboard"]',
        content: '👋 Welcome to TalentChek! This is your Dashboard. Use the sidebar to navigate the app.',
        placement: 'right',
        route: ROUTES.DASHBOARD,
      });

      // --- My Info ---
      builtSteps.push({
        target: '[data-tour="tour-my-info"]',
        content: '👤 Your Profile. Keep your personal and professional information up to date here.',
        placement: 'right',
        route: ROUTES.MY_INFO,
      });

      // --- Leave Tracker ---
      builtSteps.push({
        target: '[data-tour="tour-leave"]',
        content: '✈️ Leave Tracker. Check your balances and apply for time off.',
        placement: 'right',
        route: ROUTES.LEAVE, // Navigate to leave dashboard
      });
      builtSteps.push({
        target: '[data-tour="tour-apply-leave"]',
        content: 'Click here to apply for a new leave request. Your manager will be notified automatically.',
        placement: 'bottom',
        route: ROUTES.LEAVE,
      });

      // --- Performance ---
      builtSteps.push({
        target: '[data-tour="tour-performance"]',
        content: '⭐ Performance. Manage your goals and appraisals.',
        placement: 'right',
        route: ROUTES.PERFORMANCE_APPRAISALS,
      });
      builtSteps.push({
        target: '[data-tour="tour-create-goal"]',
        content: 'Set your KPIs and goals for the current appraisal cycle right here.',
        placement: 'bottom',
        route: ROUTES.PERFORMANCE_APPRAISALS,
      });
      builtSteps.push({
        target: '[data-tour="tour-self-rate"]',
        content: 'When it is time for reviews, come to this tab to rate your own performance.',
        placement: 'bottom',
        route: ROUTES.PERFORMANCE_APPRAISALS,
      });
      builtSteps.push({
        target: '[data-tour="tour-team-appraise"]',
        content: 'If you have direct reports, you can appraise their performance from this tab.',
        placement: 'bottom',
        route: ROUTES.PERFORMANCE_APPRAISALS,
      });

      // --- My Team ---
      builtSteps.push({
        target: '[data-tour="tour-my-team"]',
        content: '👥 View your organizational chart and team members here.',
        placement: 'right',
        route: ROUTES.MY_TEAM,
      });

      // --- Documents ---
      builtSteps.push({
        target: '[data-tour="tour-documents"]',
        content: '📄 Documents. Read and acknowledge organization policies.',
        placement: 'right',
        route: ROUTES.DOCUMENTS,
      });
      builtSteps.push({
        target: '[data-tour="tour-acknowledge-doc"]',
        content: 'When you are assigned a policy, click here to acknowledge you have read it.',
        placement: 'left',
        route: ROUTES.DOCUMENTS,
      });

      // --- Country / Global HR specific ---
      if (isCountryHR || isGlobalHR) {
        // Employees
        builtSteps.push({
          target: '[data-tour="tour-employees"]',
          content: '🏢 HR Controls: Manage the entire employee lifecycle here.',
          placement: 'right',
          route: ROUTES.EMPLOYEES,
        });
        builtSteps.push({
          target: '[data-tour="tour-add-employee"]',
          content: 'Click here to onboard a new employee into TalentChek.',
          placement: 'bottom',
          route: ROUTES.EMPLOYEES,
        });
        builtSteps.push({
          target: '[data-tour="tour-edit-employee"]',
          content: 'You can edit an employee\'s details directly from their record in this table.',
          placement: 'left',
          route: ROUTES.EMPLOYEES,
        });
        
        // Reporting Export
        builtSteps.push({
          target: '[data-tour="tour-download-report"]',
          content: 'Export your data to CSV from any reporting page using this button.',
          placement: 'bottom',
          route: ROUTES.EMPLOYEE_REPORTING,
        });

        // Leave Balances
        builtSteps.push({
          target: '[data-tour="tour-add-leave-balances"]',
          content: 'Manage and update employee leave balances from the leave reporting page.',
          placement: 'left',
          route: ROUTES.LEAVE_REPORTING,
        });
      }

      // --- Global HR specific ---
      if (isGlobalHR) {
        builtSteps.push({
          target: '[data-tour="tour-upload-policy"]',
          content: 'Upload new organization policies and assign them to employees here.',
          placement: 'bottom',
          route: ROUTES.SETTINGS_POLICIES_ADMIN,
        });
        builtSteps.push({
          target: '[data-tour="tour-add-cycle"]',
          content: 'Create and configure new Performance Appraisal Cycles for the company.',
          placement: 'bottom',
          route: ROUTES.APPRAISAL_CYCLES,
        });
      }

      setSteps(prev => {
        if (prev.length === builtSteps.length && JSON.stringify(prev) === JSON.stringify(builtSteps)) {
          return prev;
        }
        return builtSteps;
      });
      
      if (location.pathname === ROUTES.DASHBOARD) {
        setRunTutorial(true);
      }
    } else {
      setRunTutorial(false);
      setSteps([]);
      setStepIndex(0);
    }
  }, [user?.id, authenticatedEmployee, authority, isAdmin, isGlobalHR, isCountryHR, location.pathname, hasDismissedSession]);

  const handleJoyrideCallback = (data: any) => {
    const { action, index, status, type } = data;

    if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status) || action === ACTIONS.CLOSE) {
      setRunTutorial(false);
      setStepIndex(0);
      setHasDismissedSession(true);
      const roleKey = user?.employeeRole || 'employee';
      const storageKey = `tour_seen_${user?.id}_${roleKey}`;
      try {
        localStorage.setItem(storageKey, 'true');
      } catch {
        // Ignore if localStorage is unavailable
      }
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Determine the next step index
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      
      // Programmatically navigate if the route changes
      if (nextIndex >= 0 && nextIndex < steps.length) {
        const currentRoute = steps[index]?.route;
        const nextRoute = steps[nextIndex]?.route;
        
        if (nextRoute && currentRoute !== nextRoute) {
          navigate(nextRoute);
        }
      }
      
      // Update step index
      setStepIndex(nextIndex);
    }
  };

  return (
    <Joyride
      steps={steps}
      run={runTutorial}
      stepIndex={stepIndex}
      continuous={true}
      scrollToFirstStep={true}
      tooltipComponent={CustomTooltip}
      onEvent={handleJoyrideCallback}
      options={{
        zIndex: 10000,
        primaryColor: '#FAA61A',
      }}
    />
  );
}
